import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MixedSchema, initMarkdownSchema } from '@principle-theorem/editor';
import { ScribeNoteInserterService } from '@principle-theorem/ng-ai-scribe';
import { ContentViewComponent } from '@principle-theorem/ng-interactions';
import {
  BasicDialogService,
  NgDialogModule,
} from '@principle-theorem/ng-shared';
import { ScribeNote, ScribeSession } from '@principle-theorem/principle-core';
import {
  IPatient,
  IScribeNote,
  IScribeSession,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  HISTORY_DATE_TIME_FORMAT,
  INamedDocument,
  ISODateType,
  WithRef,
  safeCombineLatest,
  sortTimestampAsc,
  toMoment,
} from '@principle-theorem/shared';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

/**
 * What the review dialog is opened for: one patient's scribe notes, recorded by
 * one staffer, on one chart day. The dialog resolves the sessions itself, so
 * every caller (launcher menu, clinical-note history row, auto-open) opens the
 * exact same view for the same key.
 *
 * `staffer` is an `INamedDocument` rather than a `WithRef` because the dialog
 * only needs the ref to query and the name to display — matching
 * `IClinicalNote.owner`, which a history row already has to hand.
 *
 * `initialSessionId` is a SESSION id, not a note id: a caller knows which
 * session doc it is pointing at without reading the notes subcollection. The
 * dialog focuses that session's first note once the notes are flattened.
 */
export interface IScribeReviewDialogData {
  patient: WithRef<IPatient>;
  staffer: INamedDocument<IStaffer>;
  recordDate: ISODateType;
  initialSessionId?: string;
}

/**
 * The outcome of a review dialog. `inserted` is `true` when the practitioner
 * committed at least one scribe note into a clinical note before closing; a
 * dismissed dialog resolves to `{ inserted: false }`.
 *
 * Declared here rather than in `principle-core/interfaces` because the dialog
 * is now opened directly by its callers — there is no injection-token seam left
 * that needs a component-free description of the contract.
 */
export interface IScribeReviewDialogResult {
  inserted: boolean;
}

/** One scribe note in the flattened, chronological review list. */
interface IScribeReviewNote {
  note: WithRef<IScribeNote>;
  sessionId: string;
}

/**
 * Whether the primary Insert action is offered, and why not when it is disabled.
 * `Locked` takes precedence over `AlreadyInserted`; `NoChartTarget` covers
 * both "no clinical note is focused for this patient" and "the focused note is
 * for a different chart day", which is what makes history view-only.
 */
enum ScribeInsertState {
  Insertable = 'insertable',
  AlreadyInserted = 'already-inserted',
  NoChartTarget = 'no-chart-target',
  Locked = 'locked',
}

const INSERT_AGAIN_LABEL = 'Insert again';

/**
 * Review-and-confirm surface for the AI Scribe notes recorded for one patient,
 * staffer and chart day. It pages through the notes one at a time and lets the
 * practitioner insert the one on screen — always as an explicit click, never
 * automatically (ADR-0004).
 *
 * This is the only place the scribe notes subcollection is QUERIED; counts
 * everywhere else come from the session counters. (The management upload tool
 * does a keyed `getDoc` on a note ref it created itself, which is not a scan.)
 */
@Component({
  selector: 'pr-scribe-review-dialog',
  imports: [NgDialogModule, MatProgressSpinnerModule, ContentViewComponent],
  templateUrl: './scribe-review-dialog.component.html',
  styleUrls: ['./scribe-review-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScribeReviewDialogComponent {
  private readonly _data = inject<IScribeReviewDialogData>(MAT_DIALOG_DATA);
  private readonly _dialogRef =
    inject<
      MatDialogRef<ScribeReviewDialogComponent, IScribeReviewDialogResult>
    >(MatDialogRef);
  private readonly _inserter = inject(ScribeNoteInserterService);
  private readonly _basicDialog = inject(BasicDialogService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _inserted = signal(false);
  /** The index the practitioner paged to, or `undefined` while untouched. */
  private readonly _selectedIndex = signal<number | undefined>(undefined);
  private readonly _loadedNotes = toSignal(this._reviewNotes$(), {
    initialValue: undefined,
  });

  readonly patientName = this._data.patient.name;
  readonly practitionerName = this._data.staffer.name;
  readonly insertAgainLabel = INSERT_AGAIN_LABEL;
  /** The insert-state enum, exposed so the template can compare against it. */
  readonly insertStates = ScribeInsertState;

  readonly isLoading = computed(() => this._loadedNotes() === undefined);
  readonly notes = computed(() => this._loadedNotes() ?? []);

  /**
   * The note on screen. Falls back to the first note when the practitioner has
   * not paged yet, or when `initialSessionId` names a session that produced no
   * notes for this day.
   */
  readonly activeIndex = computed(() => {
    const notes = this.notes();
    if (notes.length === 0) {
      return 0;
    }
    const lastIndex = notes.length - 1;
    const selected = this._selectedIndex();
    if (selected !== undefined) {
      return Math.min(Math.max(selected, 0), lastIndex);
    }
    const initialSessionId = this._data.initialSessionId;
    const initialIndex =
      initialSessionId === undefined
        ? -1
        : notes.findIndex((entry) => entry.sessionId === initialSessionId);
    return initialIndex === -1 ? 0 : initialIndex;
  });

  readonly activeNote = computed(() => this.notes()[this.activeIndex()]?.note);
  readonly position = computed(() => this.activeIndex() + 1);
  readonly hasPrevious = computed(() => this.activeIndex() > 0);
  readonly hasNext = computed(
    () => this.activeIndex() < this.notes().length - 1
  );

  readonly noteDateLabel = computed(() => {
    const note = this.activeNote();
    return note === undefined
      ? ''
      : toMoment(note.createdAt).format(HISTORY_DATE_TIME_FORMAT);
  });
  readonly isInserted = computed(
    () => this.activeNote()?.insertedInto !== undefined
  );
  readonly noteContent = computed((): MixedSchema | undefined => {
    const note = this.activeNote();
    return note === undefined ? undefined : initMarkdownSchema(note.content);
  });

  /**
   * Whether a clinical note for THIS patient AND this record date is focused.
   * False from a history row for an earlier day, which is what makes history
   * view-only by construction rather than by a mode flag.
   */
  readonly hasChartTarget = toSignal(this._chartTarget$(), {
    initialValue: false,
  });
  readonly locked = toSignal(this._inserter.targetLocked$, {
    initialValue: false,
  });

  readonly insertState = computed((): ScribeInsertState => {
    if (!this.hasChartTarget()) {
      return ScribeInsertState.NoChartTarget;
    }
    if (this.locked()) {
      return ScribeInsertState.Locked;
    }
    return this.isInserted()
      ? ScribeInsertState.AlreadyInserted
      : ScribeInsertState.Insertable;
  });
  readonly canInsert = computed(
    () =>
      this.activeNote() !== undefined &&
      (this.insertState() === ScribeInsertState.Insertable ||
        this.insertState() === ScribeInsertState.AlreadyInserted)
  );

  previous(): void {
    if (this.hasPrevious()) {
      this._selectedIndex.set(this.activeIndex() - 1);
    }
  }

  next(): void {
    if (this.hasNext()) {
      this._selectedIndex.set(this.activeIndex() + 1);
    }
  }

  async insert(): Promise<void> {
    const note = this.activeNote();
    if (note === undefined || !this.canInsert()) {
      return;
    }
    if (note.insertedInto !== undefined) {
      const confirmed = await this._basicDialog.confirm({
        title: 'Insert note again?',
        prompt:
          'This note has already been inserted into a clinical note. Insert it again?',
      });
      if (confirmed !== true) {
        return;
      }
    }
    try {
      await this._inserter.insert(note);
    } catch {
      this._snackBar.open(
        'Could not insert the note — the clinical note may be locked or no longer focused.'
      );
      return;
    }
    this._inserted.set(true);
    this._dialogRef.close({ inserted: true });
  }

  dismiss(): void {
    this._dialogRef.close({ inserted: this._inserted() });
  }

  /**
   * Every undeleted note of every ready session for the dialog's key, flattened
   * oldest-first. Sessions arrive newest-first from the query, so the flattened
   * list is re-sorted on the notes' own `createdAt`.
   */
  private _reviewNotes$(): Observable<IScribeReviewNote[]> {
    return ScribeSession.readyForContext$(
      this._data.patient,
      this._data.staffer.ref,
      this._data.recordDate
    ).pipe(
      switchMap((sessions) =>
        safeCombineLatest(
          sessions.map((session) => this._sessionNotes$(session))
        )
      ),
      map((sessionNotes) =>
        sessionNotes
          .flat()
          .sort((left, right) =>
            sortTimestampAsc(left.note.createdAt, right.note.createdAt)
          )
      )
    );
  }

  private _sessionNotes$(
    session: WithRef<IScribeSession>
  ): Observable<IScribeReviewNote[]> {
    return ScribeNote.all$(session).pipe(
      map((notes) => notes.map((note) => ({ note, sessionId: session.ref.id })))
    );
  }

  /**
   * The focused clinical note must belong to this patient AND carry the same
   * `recordDate` the dialog was opened for. Reading the note document (not the
   * scribe notes subcollection) is what proves the second half.
   */
  private _chartTarget$(): Observable<boolean> {
    return this._inserter.activeRegistration$.pipe(
      switchMap((registration) => {
        if (
          registration === undefined ||
          registration.patientRef.path !== this._data.patient.ref.path
        ) {
          return of(false);
        }
        return Firestore.doc$(registration.noteRef).pipe(
          map((note) => note.recordDate === this._data.recordDate)
        );
      })
    );
  }
}
